import { Box, Paper, Typography } from "@mui/material";
import type {
	CellKeyDownEvent,
	DefaultMenuItem,
	GetContextMenuItemsParams,
	GridApi,
	MenuItemDef,
	RowClickedEvent,
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { get } from "lodash-es";
import {
	Group,
	Panel,
	Separator,
	useDefaultLayout,
} from "react-resizable-panels";
import { useSearchParams } from "react-router-dom";
import {
	ErrorModal,
	type SearchShiftsParameters,
	getMomentTz,
	getShiftsRangeByParameters,
	useContextMenuMESF,
	useLogbookSettings,
	useShiftNavigator,
	useUserContext,
} from "@dexteel/mesf-core";
import {
	CenteredLazyLoading,
	CustomStatusPanel,
	ExcelButton,
} from "@/pages/logbook/_shared";
import { getWorkOrderCountsByEntries } from "@/pages/work-orders/repositorie/WorkOrdersRepositorie";
import { useGridDefinitions } from "../useGridDefinitions";
import { type DefaultShift, useCurrentShift } from "../hooks/useCurrentShift";
import {
	LOGBOOK_QUERY_KEYS,
	type NavigationMode,
	getLogbookEntriesFilter,
	isCreatedWithin,
	useLogbookEntries,
	usePeriodBounds,
} from "../hooks/useLogbookEntries";
import { useLogbookEntryOptionsFunctions } from "../hooks/useLogbookEntryOptionsFunctions";
import { useTableData } from "../hooks/useTableData";
import type { Entry } from "../models/Entry";
import { getEntry, toEntry } from "../repository/LogbookRepository";
import { RemoveEntryDialog } from "./dialog/DeleteEntryDialog";
import { EntryViewer } from "./entry-viewer/EntryViewer";
import { Filters } from "./filters/Filters";

const moment = getMomentTz();

// Default layout matching original Grid md:5 / md:7 ratio (41.67% / 58.33%)
const DEFAULT_LAYOUT = { left: 42, right: 58 };
const CONTEXT_MENU_ID = "logbookEntryContext";

type PeriodState = { value: SearchShiftsParameters; period: string | null };

const toId = (value: string | null) => {
	const id = Number(value);
	return value && Number.isInteger(id) && id > 0 ? id : null;
};

// The shift running at a moment; the shifts table and CreateTimestamp are both UTC
const getShiftIdAt = async (at: Date | null) => {
	if (!at) return null;
	const time = at.getTime();
	const day = 24 * 60 * 60 * 1000;
	const resp = await getShiftsRangeByParameters(
		"Custom",
		new Date(time - 2 * day),
		new Date(time + 2 * day),
	);
	const shifts = get(resp, "data.tables[0].rows", []) as {
		ShiftId: number;
		Start: Date | string;
		End: Date | string;
	}[];
	const shift = shifts.find(
		(s) =>
			moment.utc(s.Start).valueOf() <= time &&
			time < moment.utc(s.End).valueOf(),
	);
	return shift?.ShiftId ?? null;
};

// Read from the live URL, not a render closure, so back-to-back updates compose
const updateUrlParams = (
	setSearchParams: ReturnType<typeof useSearchParams>[1],
	changes: Record<string, string | number | null | undefined>,
	replace = false,
) => {
	const next = new URLSearchParams(window.location.search);
	for (const [key, value] of Object.entries(changes)) {
		if (value === null || value === undefined || value === "") next.delete(key);
		else next.set(key, String(value));
	}
	setSearchParams(next, { replace });
};

export const Logbook = () => {
	const { entry: entrySettings, report: reportSettings } = useLogbookSettings();
	const {
		state: { defaultAreaId },
	} = useUserContext();
	const queryClient = useQueryClient();
	const { shiftInfo, getShiftDataFromAPI } = useShiftNavigator();
	const { registerConfig, showContextMenu } = useContextMenuMESF();
	const { data: currentShift } = useCurrentShift();

	const [searchParams, setSearchParams] = useSearchParams();
	const mode: NavigationMode =
		searchParams.get("mode") === "shift" ? "shift" : "period";
	const entryIdParam = toId(searchParams.get("entryId"));
	const action = searchParams.get("action");
	const isNewEntry = action === "new";

	const updateParams = (
		changes: Record<string, string | number | null | undefined>,
		replace = false,
	) => updateUrlParams(setSearchParams, changes, replace);

	const gridApiRef = useRef<GridApi | null>(null);
	const [error, setError] = useState("");
	const [quickFilter, setQuickFilter] = useState("");
	const [selectedEntry, setSelectedEntry] = useState<Entry | null>(null);
	const [entryToDelete, setEntryToDelete] = useState<Entry | null>(null);
	const [newEntryPreview, setNewEntryPreview] = useState(false);

	const [assetId, setAssetId] = useState<number | null>(
		entrySettings.presetAssetId ??
			(typeof defaultAreaId === "number" ? defaultAreaId : null),
	);
	const initializedAssetRef = useRef(false);

	// Shift mode: the global shift navigator, ignored until this page loads its own shift
	const [shiftReady, setShiftReady] = useState(false);
	const viewedShiftId = shiftReady ? (shiftInfo?.CurrentShiftId ?? null) : null;

	// Period mode: kept here too, so switching modes and back restores it
	const [periodState, setPeriodState] = useState<PeriodState>(() => ({
		value: {
			StartShiftId: toId(searchParams.get("startShiftId")),
			EndShiftId: toId(searchParams.get("endShiftId")),
		},
		period: searchParams.get("period"),
	}));
	const [periodNavigatorKey, setPeriodNavigatorKey] = useState(0);
	const isAllPeriod = periodState.period === "All";
	// The navigator normalizes URL ids to whole days on mount ("All" never reports);
	// wait for it so a partial range is not loaded, selected and then closed
	const [periodReady, setPeriodReady] = useState(isAllPeriod);

	const entriesProps = { mode, shiftId: viewedShiftId, assetId };
	const {
		data: loadedRows,
		isFetching: isFetchingEntries,
		error: queryError,
		isError,
	} = useLogbookEntries(entriesProps);

	// Period mode shows the entries created inside the period ("All" shows every entry);
	// shift mode shows the entries assigned to the shift
	const filtersByCreation = mode === "period" && !isAllPeriod;
	const {
		data: periodBounds,
		isFetching: isFetchingBounds,
		error: boundsError,
		isError: isBoundsError,
	} = usePeriodBounds(
		filtersByCreation && periodReady ? periodState.value.StartShiftId : null,
		filtersByCreation && periodReady ? periodState.value.EndShiftId : null,
	);
	const rows = useMemo(() => {
		if (!loadedRows || !filtersByCreation) return loadedRows;
		if (!periodReady || isFetchingBounds) return undefined;
		return periodBounds
			? loadedRows.filter((entry) => isCreatedWithin(entry, periodBounds))
			: [];
	}, [
		loadedRows,
		filtersByCreation,
		periodReady,
		isFetchingBounds,
		periodBounds,
	]);
	const isFetching =
		isFetchingEntries ||
		(filtersByCreation && (!periodReady || isFetchingBounds));
	// read by the save handler, which runs after an await
	const visibilityRef = useRef({ filtersByCreation, periodBounds });
	visibilityRef.current = { filtersByCreation, periodBounds };
	const listKeyRef = useRef(
		LOGBOOK_QUERY_KEYS.list(getLogbookEntriesFilter(entriesProps)),
	);
	listKeyRef.current = LOGBOOK_QUERY_KEYS.list(
		getLogbookEntriesFilter(entriesProps),
	);

	// The time window on screen; an open entry closes when the user moves it
	const windowKey =
		mode === "shift"
			? `shift:${viewedShiftId}`
			: `period:${periodState.value.StartShiftId}:${periodState.value.EndShiftId}:${isAllPeriod}`;
	const previousWindowKeyRef = useRef(windowKey);
	const windowKeyRef = useRef(windowKey);
	windowKeyRef.current = windowKey;
	// Set while the navigator moves to a just-saved entry, so that move does not close it
	const revealRef = useRef<{ entryId: number; windowKey: string } | null>(null);

	// counts come from a separate MMS query (LB.GetEntries can not reference MMS.WorkOrders)
	const entryIdsKey = (rows ?? [])
		.map((r) => r.EntryId)
		.filter(Boolean)
		.join(",");
	const { data: woCountsResp } = useQuery({
		queryKey: ["entryWorkOrderCounts", entryIdsKey],
		queryFn: () =>
			getWorkOrderCountsByEntries(
				(rows ?? []).map((r) => r.EntryId).filter(Boolean) as number[],
			),
		enabled: !!entryIdsKey,
	});
	const rowsWithWorkOrders = useMemo(() => {
		const map = new Map<number, number>();
		const countRows = get(woCountsResp, "data.tables[0].rows", []) as {
			EntryId: number;
			WorkOrderCount: number;
		}[];
		for (const c of countRows) map.set(c.EntryId, c.WorkOrderCount);
		return (rows ?? []).map((r) => ({
			...r,
			WorkOrderCount: map.get(r.EntryId as number) ?? 0,
		}));
	}, [rows, woCountsResp]);

	// A new entry starts on the shift being viewed (shift mode) or the one running now
	const defaultShift = useMemo<DefaultShift | null>(() => {
		const navigatorShift = shiftInfo?.CurrentShiftId
			? {
					ShiftId: shiftInfo.CurrentShiftId,
					Shift: shiftInfo.CurrentShift ?? null,
					Crew: shiftInfo.CurrentCrew ?? null,
				}
			: null;
		if (mode === "shift") return navigatorShift;
		return currentShift ?? navigatorShift;
	}, [
		mode,
		currentShift,
		shiftInfo?.CurrentShiftId,
		shiftInfo?.CurrentShift,
		shiftInfo?.CurrentCrew,
	]);

	const { columnDefs } = useTableData({
		showContextMenu,
		allowAttachments: entrySettings.allowAttachments,
		showAsset: reportSettings.showAsset,
		showShiftCrew: reportSettings.showShiftCrew,
	});
	const { defaultColDef } = useGridDefinitions({});

	// Resizable panels layout persistence
	const { defaultLayout: savedLayout, onLayoutChange } = useDefaultLayout({
		id: "logbook-v1",
		storage: localStorage,
	});
	// Use saved layout or default to avoid flash on initial render
	const resolvedLayout = savedLayout ?? DEFAULT_LAYOUT;

	const selectRow = (entryId: number) => {
		const node = gridApiRef.current?.getRowNode(String(entryId));
		if (node) {
			node.setSelected(true);
			gridApiRef.current?.ensureNodeVisible(node);
		}
	};

	const openEntry = (entry: Entry, nextAction: "view" | "edit" = "view") => {
		setSelectedEntry(entry);
		selectRow(entry.EntryId);
		updateParams({ entryId: entry.EntryId, action: nextAction });
	};

	const closeViewer = () => {
		setSelectedEntry(null);
		gridApiRef.current?.deselectAll();
		updateParams({ entryId: null, action: null });
	};

	const handleNewEntry = () => {
		setSelectedEntry(null);
		setNewEntryPreview(false);
		gridApiRef.current?.deselectAll();
		updateParams({ entryId: null, action: "new" });
	};

	const handleModeChange = (nextMode: NavigationMode) => {
		if (nextMode === mode) return;
		updateParams(
			nextMode === "shift"
				? {
						mode: "shift",
						shiftId: viewedShiftId,
						startShiftId: null,
						endShiftId: null,
						period: null,
					}
				: {
						mode: null,
						shiftId: null,
						startShiftId: periodState.value.StartShiftId,
						endShiftId: periodState.value.EndShiftId,
						period: periodState.period,
					},
		);
	};

	const modeRef = useRef(mode);
	modeRef.current = mode;
	const handlePeriodChange = (
		value: SearchShiftsParameters,
		period: string | null,
	) => {
		setPeriodState({ value, period });
		setPeriodReady(true);
		// the period navigator also runs while hidden; only period mode owns the URL
		if (modeRef.current === "period") {
			updateParams(
				{
					startShiftId: value.StartShiftId,
					endShiftId: value.EndShiftId,
					period,
				},
				true,
			);
		}
	};

	// After a save: refresh, show the fresh entry, and bring it into view if it
	// landed outside the window on screen (other shift, other period, "All" search)
	const handleSaved = async (saved: Entry, shouldStayInEditMode = false) => {
		const entryId = saved.EntryId;
		// A just-created entry is shown from the save response until its row loads, so the
		// viewer (and an attachment upload or work order it started) is not unmounted
		setSelectedEntry((prev) =>
			prev?.EntryId === entryId ? prev : toEntry(saved),
		);
		updateParams({
			entryId,
			action: shouldStayInEditMode ? "edit" : "view",
		});
		try {
			await queryClient.invalidateQueries(LOGBOOK_QUERY_KEYS.all);
			const loaded = queryClient
				.getQueryData<Entry[]>(listKeyRef.current)
				?.find((e) => e.EntryId === entryId);
			const { filtersByCreation: byCreation, periodBounds: bounds } =
				visibilityRef.current;
			const inList =
				loaded && (!byCreation || (!!bounds && isCreatedWithin(loaded, bounds)))
					? loaded
					: undefined;
			const fresh = loaded ?? (await getEntry(entryId));
			if (!fresh) return;
			setSelectedEntry(fresh);
			if (inList) {
				selectRow(entryId);
				return;
			}

			// Not in the list: move the navigator to the entry, unless it already shows that
			// window and the entry is only hidden by the asset filter or the search
			if (modeRef.current === "shift") {
				const target = `shift:${fresh.ShiftId}`;
				if (target === windowKeyRef.current) return;
				revealRef.current = { entryId, windowKey: target };
				await getShiftDataFromAPI(null, fresh.ShiftId);
				return;
			}
			// Period mode goes to the day the entry was created, not the shift it is assigned to
			const createdShiftId =
				(await getShiftIdAt(fresh.CreateTimestamp)) ?? fresh.ShiftId;
			const resp = await getShiftsRangeByParameters(
				"Day",
				null,
				null,
				createdShiftId,
				createdShiftId,
			);
			const dayShifts = get(resp, "data.tables[0].rows", []) as {
				ShiftId: number;
			}[];
			const value = {
				StartShiftId: dayShifts[0]?.ShiftId ?? createdShiftId,
				EndShiftId: dayShifts[dayShifts.length - 1]?.ShiftId ?? createdShiftId,
			};
			const target = `period:${value.StartShiftId}:${value.EndShiftId}:false`;
			if (target === windowKeyRef.current) return;
			revealRef.current = { entryId, windowKey: target };
			setPeriodState({ value, period: "Day" });
			setPeriodNavigatorKey((key) => key + 1);
			updateParams(
				{
					startShiftId: value.StartShiftId,
					endShiftId: value.EndShiftId,
					period: "Day",
				},
				true,
			);
		} catch (e: any) {
			setError(e?.message ?? String(e));
		}
	};

	const rowClicked = (event: RowClickedEvent) => {
		if (event.data) openEntry(event.data);
	};

	const onCellKeyDown = (event: CellKeyDownEvent) => {
		const { api, node, event: keyEvent } = event;
		if (!keyEvent) return;
		const key = (keyEvent as KeyboardEvent).key;

		if (key === "ArrowUp" || key === "ArrowDown") {
			keyEvent.preventDefault();
			const currentIndex = node.rowIndex !== null ? node.rowIndex : -1;
			const nextRowIndex =
				key === "ArrowUp" ? currentIndex - 1 : currentIndex + 1;
			const nextNode = api.getDisplayedRowAtIndex(nextRowIndex);

			if (nextNode?.data) {
				api.ensureIndexVisible(nextRowIndex);
				openEntry(nextNode.data);
			}
		}
	};

	// Context menu handlers change every render; the menu is registered once
	const menuHandlersRef = useRef({
		onNewEntry: handleNewEntry,
		onEditEntry: (entry: Entry) => openEntry(entry, "edit"),
		onDeleteEntry: (entry: Entry) => setEntryToDelete(entry),
	});
	menuHandlersRef.current = {
		onNewEntry: handleNewEntry,
		onEditEntry: (entry: Entry) => openEntry(entry, "edit"),
		onDeleteEntry: (entry: Entry) => setEntryToDelete(entry),
	};
	const { getMenuOptions } = useLogbookEntryOptionsFunctions({
		onNewEntry: () => menuHandlersRef.current.onNewEntry(),
		onEditEntry: (entry) => menuHandlersRef.current.onEditEntry(entry),
		onDeleteEntry: (entry) => menuHandlersRef.current.onDeleteEntry(entry),
	});

	const getContextMenuItems = useCallback(
		(params: GetContextMenuItemsParams): (DefaultMenuItem | MenuItemDef)[] => {
			const data = params.node?.data;
			showContextMenu(event as any, data, CONTEXT_MENU_ID);
			if (data) {
				params.api.deselectAll();
				params.node?.setSelected(true);
			}
			return [];
		},
		[showContextMenu],
	);

	// Pick the URL entry up once its row is rendered (deep links, saves)
	const syncSelection = useCallback(() => {
		const api = gridApiRef.current;
		if (!api || entryIdParam === null || isNewEntry) return;
		const node = api.getRowNode(String(entryIdParam));
		if (!node) return;
		if (!node.isSelected()) {
			node.setSelected(true);
			api.ensureNodeVisible(node);
		}
		if (selectedEntry?.EntryId !== entryIdParam) setSelectedEntry(node.data);
	}, [entryIdParam, isNewEntry, selectedEntry?.EntryId]);

	const handleExportToExcel = () => {
		if (!gridApiRef.current) return;

		gridApiRef.current.exportDataAsExcel({
			fileName: "Logbook_Export.xlsx",
			columnKeys: [
				"AssetName",
				"CreateTimestamp",
				"Shift",
				"Crew",
				"Title",
				"Author",
				"Content",
			],
			processCellCallback: (params: any) => {
				if (params.column.colId === "CreateTimestamp") {
					return moment(params.value).format("MM/DD/yy - HH:mm");
				}
				return params.value;
			},
		});
	};

	// Load the shift from the URL, or the current one
	// biome-ignore lint/correctness/useExhaustiveDependencies: once on mount
	useEffect(() => {
		(async () => {
			await getShiftDataFromAPI(null, toId(searchParams.get("shiftId")) ?? 0);
			setShiftReady(true);
		})();
	}, []);

	// biome-ignore lint/correctness/useExhaustiveDependencies: options read the latest handlers through a ref
	useEffect(() => {
		registerConfig({
			id: CONTEXT_MENU_ID,
			getOptions: getMenuOptions,
		});
	}, []);

	// Keep the shift in the URL while browsing by shift
	// biome-ignore lint/correctness/useExhaustiveDependencies: only when the shift or the mode changes
	useEffect(() => {
		if (mode === "shift" && viewedShiftId) {
			updateParams({ shiftId: viewedShiftId }, true);
		}
	}, [mode, viewedShiftId]);

	// biome-ignore lint/correctness/useExhaustiveDependencies: only when the window on screen changes
	useEffect(() => {
		if (previousWindowKeyRef.current === windowKey) return;
		previousWindowKeyRef.current = windowKey;
		if (revealRef.current?.windowKey === windowKey) return;
		if (selectedEntry) closeViewer();
	}, [windowKey]);

	// The reveal is over once the target window has loaded
	// biome-ignore lint/correctness/useExhaustiveDependencies: selectRow only touches the grid api
	useEffect(() => {
		const reveal = revealRef.current;
		if (!reveal || reveal.windowKey !== windowKey || isFetching || !rows)
			return;
		revealRef.current = null;
		selectRow(reveal.entryId);
	}, [windowKey, isFetching, rows]);

	// biome-ignore lint/correctness/useExhaustiveDependencies: rows is the trigger, the grid holds the data
	useEffect(() => {
		syncSelection();
	}, [syncSelection, rows]);

	// The URL entry changed from outside (back button, links): drop a viewer showing
	// another entry; syncSelection (which runs first) already picked the new one if loaded
	useEffect(() => {
		if (isNewEntry) return;
		if (entryIdParam === null) gridApiRef.current?.deselectAll();
		setSelectedEntry((prev) =>
			prev && prev.EntryId !== entryIdParam ? null : prev,
		);
	}, [entryIdParam, isNewEntry]);

	useEffect(() => {
		if (isError && queryError) {
			setError(queryError.message);
		}
	}, [isError, queryError]);

	useEffect(() => {
		if (isBoundsError && boundsError) {
			setError(boundsError.message);
		}
	}, [isBoundsError, boundsError]);

	// Sync assetId with defaultAreaId when it becomes available (only once on initial load)
	useEffect(() => {
		if (
			!initializedAssetRef.current &&
			entrySettings.presetAssetId == null &&
			typeof defaultAreaId === "number"
		) {
			setAssetId(defaultAreaId);
			initializedAssetRef.current = true;
		}
	}, [defaultAreaId, entrySettings.presetAssetId]);

	const viewerShow: "edit" | "view" | "" = isNewEntry
		? newEntryPreview
			? "view"
			: "edit"
		: selectedEntry
			? action === "edit"
				? "edit"
				: "view"
			: "";

	return (
		<Box
			sx={{
				width: "100%",
				// match the standard page layout (see Work Orders)
				padding: "10px 20px",
				height: "calc(100vh - 64px)",
				boxSizing: "border-box",
				display: "flex",
				flexDirection: "column",
				flexWrap: "nowrap",
			}}
		>
			<Box sx={{ mb: 1, flexShrink: 0 }}>
				<Typography
					variant="h5"
					style={{
						margin: 0,
						marginLeft: 5,
						fontWeight: 600,
						userSelect: "none",
					}}
				>
					LOGBOOK
				</Typography>
			</Box>
			<Group
				id="logbook-group"
				orientation="horizontal"
				defaultLayout={resolvedLayout}
				onLayoutChange={onLayoutChange}
				style={{ flex: 1, width: "100%" }}
			>
				<Panel
					id="left"
					defaultSize={42}
					minSize={25}
					style={{ paddingBottom: 5 }}
				>
					<Box
						sx={{
							display: "flex",
							flexDirection: "column",
							gap: 1,
							height: "100%",
							pr: 1,
						}}
					>
						<Filters
							mode={mode}
							onModeChange={handleModeChange}
							withAssetFilter={reportSettings.withAssetFilter}
							filterAssets={reportSettings.filterAssets}
							quickFilter={quickFilter}
							onQuickFilterChange={setQuickFilter}
							assetId={assetId}
							setAssetId={setAssetId}
							shiftPeriod={periodState.value}
							onChangeShiftPeriod={handlePeriodChange}
							actualPeriod={periodState.period}
							periodNavigatorKey={periodNavigatorKey}
						/>
						<Paper
							elevation={1}
							sx={{
								flex: 1,
								width: "100%",
								// Remove the AG Grid cell-focus outline in this grid.
								"& .ag-cell-focus, & .ag-cell:focus, & .ag-cell-focus:focus": {
									border: "none !important",
									outline: "none !important",
									boxShadow: "none !important",
								},
								// the global theme zeroes cell padding, which leaves the text
								// against the cell edges; give it a little room back
								"& .ag-cell": { padding: "0 6px !important" },
								"& .ag-header-cell": {
									paddingLeft: "6px",
									paddingRight: "6px",
								},
							}}
						>
							<AgGridReact<Entry>
								loading={isFetching}
								rowData={rowsWithWorkOrders}
								quickFilterText={quickFilter}
								columnDefs={columnDefs}
								defaultColDef={defaultColDef}
								rowHeight={38}
								headerHeight={42}
								loadingOverlayComponent={CenteredLazyLoading}
								animateRows
								getContextMenuItems={getContextMenuItems}
								onRowClicked={rowClicked}
								onCellKeyDown={onCellKeyDown}
								onGridReady={(params) => (gridApiRef.current = params.api)}
								onRowDataUpdated={syncSelection}
								getRowId={(params) => `${params.data.EntryId}`}
								gridOptions={{
									// the search also looks in the entry text (hidden column)
									includeHiddenColumnsInQuickFilter: true,
									rowSelection: {
										mode: "singleRow",
										checkboxes: false,
										enableClickSelection: true,
									},
									statusBar: {
										statusPanels: [
											{
												statusPanel: () => (
													<CustomStatusPanel handleNew={handleNewEntry} />
												),
												align: "left",
											},
											reportSettings.exportToExcel
												? {
														statusPanel: () => (
															<div
																style={{
																	display: "flex",
																	alignItems: "center",
																	// keep the button off the status bar edge
																	paddingTop: 5,
																	paddingBottom: 3,
																}}
															>
																<ExcelButton
																	handleExportToExcel={handleExportToExcel}
																/>
															</div>
														),
														align: "right",
													}
												: {},
										],
									},
								}}
							/>
						</Paper>
					</Box>
				</Panel>
				<Separator
					style={{
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						cursor: "col-resize",
					}}
				>
					<Box
						sx={{
							width: 8,
							height: 48,
							borderRadius: 1,
							backgroundColor: "#e0e0e0",
							display: "flex",
							flexDirection: "column",
							alignItems: "center",
							justifyContent: "center",
							gap: "3px",
							transition: "background-color 0.2s",
							"&:hover": {
								backgroundColor: "primary.main",
								"& .grip-dot": {
									backgroundColor: "#fff",
								},
							},
						}}
					>
						{[...Array(5)].map((_, i) => (
							<Box
								key={i}
								className="grip-dot"
								sx={{
									width: 4,
									height: 4,
									borderRadius: "50%",
									backgroundColor: "#9e9e9e",
									transition: "background-color 0.2s",
								}}
							/>
						))}
					</Box>
				</Separator>
				<Panel
					id="right"
					defaultSize={58}
					minSize={30}
					style={{ paddingBottom: 5 }}
				>
					<Box sx={{ pl: 1, height: "100%" }}>
						<EntryViewer
							entry={isNewEntry ? null : selectedEntry}
							isNewEntry={isNewEntry}
							show={viewerShow}
							defaultShift={defaultShift}
							onChangeShow={() => {
								if (isNewEntry) setNewEntryPreview((preview) => !preview);
								else
									updateParams({ action: action === "edit" ? "view" : "edit" });
							}}
							onHide={closeViewer}
							onSaved={handleSaved}
							preselectedAssetId={assetId}
							withAssetFilter={entrySettings.withAssetFilter}
							canEditAsset={entrySettings.canEditAsset}
							canEditShift={entrySettings.canEditShift}
							filterAssets={entrySettings.filterAssets}
							allowAttachments={entrySettings.allowAttachments}
							shiftsRange={entrySettings.shiftsRange}
						/>
					</Box>
				</Panel>
			</Group>
			<RemoveEntryDialog
				entry={entryToDelete}
				show={entryToDelete !== null}
				onHide={(deleted) => {
					if (deleted && entryToDelete?.EntryId === selectedEntry?.EntryId) {
						closeViewer();
					}
					setEntryToDelete(null);
				}}
			/>
			<ErrorModal error={error} onHide={() => setError("")} />
		</Box>
	);
};
